import type { SignupGateMode, StrategyStatus } from '@arthelper/shared/acquisition/strategy';
import type { Strategy, StrategyMetrics } from './queries';

const STATUS_PILLS: Record<StrategyStatus, string> = {
  active: 'bg-green-100 text-green-800',
  draft: 'bg-gray-100 text-gray-700',
  archived: 'bg-amber-100 text-amber-800',
};

export const GATE_LABELS: Record<SignupGateMode, string> = {
  open: 'Open signup',
  refer_to_join: 'Refer to join',
  invite_required: 'Invite required',
};

export function StatusBadge({ status }: { status: StrategyStatus }) {
  return (
    <span className={`rounded-full px-2 py-1 text-xs font-medium capitalize ${STATUS_PILLS[status]}`}>{status}</span>
  );
}

/** `n / d` as a percentage, or "—" when there is nothing to divide by yet. */
export function percent(n: number, d: number): string {
  if (d === 0) {
    return '—';
  }
  return `${((n / d) * 100).toFixed(1)}%`;
}

function ratio(n: number, d: number): string {
  if (d === 0) {
    return '—';
  }
  return (n / d).toFixed(2);
}

export interface MetricRow {
  label: string;
  hint?: string;
  value: (m: StrategyMetrics) => string;
}

export const METRIC_ROWS: MetricRow[] = [
  { label: 'Visitors', value: (m) => m.visitors.toLocaleString() },
  { label: 'Signups', value: (m) => m.signups.toLocaleString() },
  { label: 'Signup rate', hint: 'signups / visitors', value: (m) => percent(m.signups, m.visitors) },
  { label: 'Waitlisted', value: (m) => m.waitlisted.toLocaleString() },
  { label: 'Unlocked', value: (m) => m.unlocked.toLocaleString() },
  { label: 'Referred in', value: (m) => m.referredIn.toLocaleString() },
  { label: 'K-factor', hint: 'referrals made / signups', value: (m) => ratio(m.referralsMade, m.signups) },
  { label: 'Activation', hint: 'activated / signups', value: (m) => percent(m.activated, m.signups) },
  { label: 'D7 retention', hint: 'retained / eligible at day 7', value: (m) => percent(m.retainedD7, m.eligibleD7) },
];

export function formatDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleDateString();
}

export function label(strategy: Pick<Strategy, 'version' | 'name'>): string {
  return `v${strategy.version} ${strategy.name}`;
}

/** Apollo surfaces the server's BAD_USER_INPUT message as `err.message`; show it verbatim. */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}
